import { useMemo } from 'react';

const RANK_STYLES = [
  { border: 'border-[#FFD700]', bg: 'bg-gradient-to-br from-yellow-300 to-yellow-600', text: 'text-yellow-900', glow: 'shadow-[0_0_20px_rgba(255,215,0,0.4)]', scoreColor: 'text-[#FFD700]', label: '1.' },
  { border: 'border-[#C0C0C0]', bg: 'bg-gradient-to-br from-slate-300 to-slate-500', text: 'text-slate-800', glow: 'shadow-[0_0_15px_rgba(192,192,192,0.3)]', scoreColor: 'text-[#C0C0C0]', label: '2.' },
  { border: 'border-[#CD7F32]', bg: 'bg-gradient-to-br from-orange-400 to-orange-700', text: 'text-orange-900', glow: 'shadow-[0_0_15px_rgba(205,127,50,0.3)]', scoreColor: 'text-[#CD7F32]', label: '3.' },
];

export default function RebusScoreboard({ session, rebus, embedded = false, onBack }) {
  const rankedTeams = useMemo(() => {
    if (!session?.teams) return [];
    return Object.values(session.teams)
      .map(team => {
        // Calculate score breakdown
        let stationPoints = 0;
        let challengePoints = 0;
        let extraPoints = 0;

        const stationResults = team.stationResults || {};
        for (const stationResult of Object.values(stationResults)) {
          const answers = stationResult.answers || {};
          for (const answer of Object.values(answers)) {
            stationPoints += answer.points || 0;
          }
          if (typeof stationResult.challengeScore === 'number') {
            challengePoints += stationResult.challengeScore;
          }
        }

        const extraResults = team.extraTaskResults || {};
        for (const result of Object.values(extraResults)) {
          if (result.approved === true) {
            extraPoints += result.points || 0;
          }
        }

        const bonusPoints = team.bonusPoints || 0;

        return {
          ...team,
          stationPoints,
          challengePoints,
          extraPoints,
          bonusPoints,
          totalScore: stationPoints + challengePoints + extraPoints + bonusPoints,
        };
      })
      .sort((a, b) => b.totalScore - a.totalScore);
  }, [session?.teams]);

  const getInitial = (name) => name?.charAt(0)?.toUpperCase() || '?';

  // Embedded version (inside tab)
  if (embedded) {
    return (
      <div className="space-y-3">
        {rankedTeams.length === 0 ? (
          <div className="text-center py-8">
            <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">leaderboard</span>
            <p className="text-gray-500 font-medium">Ingen lag enno</p>
          </div>
        ) : (
          rankedTeams.map((team, index) => {
            const isTop3 = index < 3;
            const style = RANK_STYLES[index];

            return (
              <div
                key={team.id}
                className={`bg-white rounded-xl shadow-ambient p-4 ${
                  isTop3 ? `ring-2 ${style.border.replace('border-', 'ring-')}` : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`shrink-0 size-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    isTop3 ? `${style.bg} ${style.text}` : 'bg-gray-100 text-gray-600'
                  }`}>
                    {isTop3 ? getInitial(team.name) : index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#0d121b] truncate">{team.name}</p>
                    <div className="flex gap-3 text-[10px] text-gray-400 font-medium mt-0.5">
                      <span>Stasjon: {team.stationPoints}</span>
                      <span>Utfordring: {team.challengePoints}</span>
                      <span>Ekstra: {team.extraPoints}</span>
                      {team.bonusPoints !== 0 && <span>Bonus: {team.bonusPoints > 0 ? '+' : ''}{team.bonusPoints}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xl font-extrabold ${isTop3 ? style.scoreColor : 'text-gray-600'}`}>
                      {team.totalScore}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase font-bold">poeng</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  }

  // Full-screen scoreboard (dark premium theme)
  return (
    <div className="font-display min-h-screen text-white">
      <div className="relative flex min-h-screen w-full flex-col bg-[#1a1122] overflow-hidden">
        {/* Spotlight Gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,_#064e3b_0%,_#191022_70%)] opacity-80 pointer-events-none"></div>

        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 pointer-events-none opacity-10"
          style={{
            backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>

        {/* Header */}
        <div className="relative z-10 flex flex-col items-center text-center pt-8 pb-4 px-4">
          <p className="text-white/40 text-xs font-bold tracking-widest uppercase mb-2">
            {rebus?.title || 'Rebus'}
          </p>
          <h1 className="text-white text-4xl md:text-5xl font-extrabold tracking-tight drop-shadow-lg">
            Resultattavle
          </h1>
        </div>

        {/* Podium for top 3 */}
        {rankedTeams.length >= 3 && (
          <div className="relative z-10 mx-auto w-full max-w-3xl px-6 mb-6">
            <div className="flex items-end justify-center gap-3">
              {/* 2nd place */}
              <PodiumBlock team={rankedTeams[1]} rank={2} height="h-28" />
              {/* 1st place */}
              <PodiumBlock team={rankedTeams[0]} rank={1} height="h-36" />
              {/* 3rd place */}
              <PodiumBlock team={rankedTeams[2]} rank={3} height="h-24" />
            </div>
          </div>
        )}

        {/* Full ranked list */}
        <div className="relative z-10 flex-1 w-full max-w-3xl mx-auto px-6 pb-24">
          {rankedTeams.length === 0 ? (
            <div className="text-center text-white/50 text-2xl py-12">
              Ingen lag enno
            </div>
          ) : (
            <div className="space-y-3">
              {rankedTeams.map((team, index) => {
                const isTop3 = index < 3;
                const style = RANK_STYLES[index];

                return (
                  <div
                    key={team.id}
                    className="animate-slide-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className={`flex items-center gap-4 rounded-2xl px-5 py-4 transition-all duration-300 ${
                      isTop3
                        ? 'bg-white/10 backdrop-blur-md border border-white/10'
                        : 'bg-white/5 border border-white/5'
                    }`}>
                      {/* Rank Avatar */}
                      <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl border-3 ${
                        isTop3
                          ? `${style.bg} ${style.border} border-4 ${style.glow}`
                          : 'bg-white/10 border-2 border-white/20 text-white/60'
                      }`}>
                        {isTop3 ? (
                          <span className={style.text}>{getInitial(team.name)}</span>
                        ) : (
                          <span className="text-lg">{index + 1}</span>
                        )}
                      </div>

                      {/* Rank badge for top 3 */}
                      {isTop3 && (
                        <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-extrabold text-xs ${
                          index === 0 ? 'bg-[#FFD700] text-[#1a1122]' :
                          index === 1 ? 'bg-[#C0C0C0] text-[#1a1122]' :
                          'bg-[#CD7F32] text-[#1a1122]'
                        }`}>
                          {index + 1}
                        </div>
                      )}

                      {/* Name + breakdown */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-extrabold truncate ${
                          index === 0 ? 'text-xl' : isTop3 ? 'text-lg' : 'text-base text-white/80'
                        }`}>
                          {team.name}
                        </p>
                        <div className="flex gap-3 text-[10px] text-white/40 font-medium mt-0.5">
                          <span>Stasjon: {team.stationPoints}</span>
                          <span>Utfordring: {team.challengePoints}</span>
                          <span>Ekstra: {team.extraPoints}</span>
                          {team.bonusPoints !== 0 && <span>Bonus: {team.bonusPoints > 0 ? '+' : ''}{team.bonusPoints}</span>}
                        </div>
                      </div>

                      {/* Score */}
                      <div className="shrink-0 text-right">
                        <p className={`font-extrabold tabular-nums ${
                          isTop3 ? `text-2xl ${style.scoreColor}` : 'text-xl text-white/60'
                        }`}>
                          {team.totalScore}
                        </p>
                        <p className="text-white/30 text-[10px] uppercase font-bold tracking-wider">poeng</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Back button */}
        {onBack && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
            <button
              onClick={onBack}
              className="px-8 py-3 bg-white/20 backdrop-blur-md text-white rounded-full font-bold hover:bg-white/30 transition-colors border border-white/20"
            >
              Tilbake
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PodiumBlock({ team, rank, height }) {
  const style = RANK_STYLES[rank - 1];
  return (
    <div className="flex flex-col items-center w-28">
      {/* Avatar */}
      <div className={`size-14 rounded-full flex items-center justify-center font-bold text-xl border-4 mb-2 ${style.bg} ${style.border} ${style.glow}`}>
        <span className={style.text}>{team.name?.charAt(0)?.toUpperCase() || '?'}</span>
      </div>
      <p className="text-white font-bold text-sm truncate w-full text-center mb-1">{team.name}</p>
      <p className={`font-extrabold text-lg ${style.scoreColor}`}>{team.totalScore}</p>
      {/* Podium bar */}
      <div className={`w-full ${height} rounded-t-xl mt-1 ${
        rank === 1 ? 'bg-gradient-to-t from-yellow-600/30 to-yellow-400/20 border border-yellow-500/30' :
        rank === 2 ? 'bg-gradient-to-t from-slate-500/30 to-slate-300/20 border border-slate-400/30' :
        'bg-gradient-to-t from-orange-600/30 to-orange-400/20 border border-orange-500/30'
      } flex items-center justify-center`}>
        <span className={`text-3xl font-extrabold ${style.scoreColor} opacity-50`}>{rank}</span>
      </div>
    </div>
  );
}
